// MetricsDashboard.js
import React, {useState, useEffect} from "react"
import {ref, get} from "firebase/database"
import {database} from "../firebase"
import UserPathsVisualization from "./UserPathsVisualization";
import {
    calculateUserSegmentation,
    getExperienceDistribution,
    getTopApps,
    APP_LABELS,
    EXPERIENCE_LABELS
} from "../utils/userSegmentationUtils";

const MetricsDashboard = () => {
    const [loading, setLoading] = useState(true)
    const [todaysUsers, setTodaysUsers] = useState([])
    const [expandedUser, setExpandedUser] = useState(null)
    const [platformStats, setPlatformStats] = useState({ios: 0, android: 0})
    // Add state for visit metrics
    const [visitMetrics, setVisitMetrics] = useState({
        todayVisits: 0,
        yesterdayVisits: 0,
        totalVisits: 0,
        sourceBreakdown: {},
    })
    // New state for recent visits
    const [recentVisits, setRecentVisits] = useState([])
    // State for user segmentation
    const [segmentationStats, setSegmentationStats] = useState(null)

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true)

                // Fetch users data (existing code)
                await fetchTodaysNewUsers()

                // Fetch visit metrics and recent visits
                await fetchVisitMetrics()

                // Fetch segmentation data
                await fetchSegmentationData()

                setLoading(false)
            } catch (error) {
                console.error("Error fetching dashboard data:", error)
                setLoading(false)
            }
        }

        fetchData()

        // Refresh data every 2 minutes
        const intervalId = setInterval(fetchData, 2 * 60 * 1000)

        return () => clearInterval(intervalId)
    }, [])

    // Fetch visit metrics from Firebase
    const fetchVisitMetrics = async () => {
        try {
            // Calculate today's and yesterday's date
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            const todayStr = today.toISOString().split("T")[0] // Format: YYYY-MM-DD

            const yesterday = new Date(today)
            yesterday.setDate(yesterday.getDate() - 1)
            const yesterdayStr = yesterday.toISOString().split("T")[0]

            // Fetch daily visitors counts
            const dailyVisitorsRef = ref(database, "metrics/dailyVisitors")
            const dailyVisitorsSnapshot = await get(dailyVisitorsRef)

            let todayVisits = 0
            let yesterdayVisits = 0

            if (dailyVisitorsSnapshot.exists()) {
                const dailyData = dailyVisitorsSnapshot.val()

                if (dailyData[todayStr]) {
                    todayVisits = dailyData[todayStr].count || 0
                }

                if (dailyData[yesterdayStr]) {
                    yesterdayVisits = dailyData[yesterdayStr].count || 0
                }
            }

            // Fetch detailed visit data to calculate source breakdown
            const visitsRef = ref(database, "metrics/visits")
            const visitsSnapshot = await get(visitsRef)

            const anonymousVisitsRef = ref(database, "metrics/anonymousVisits")
            const anonymousVisitsSnapshot = await get(anonymousVisitsRef)

            let totalVisits = 0
            const sourceBreakdown = {}
            let allVisits = []

            // Process authenticated visits
            if (visitsSnapshot.exists()) {
                const visits = visitsSnapshot.val()

                // Iterate through users
                Object.entries(visits).forEach(([userId, userVisits]) => {
                    // Each user may have multiple visits
                    Object.entries(userVisits).forEach(([visitId, visit]) => {
                        totalVisits++

                        // Count by source
                        const source = visit.source || "unknown"
                        sourceBreakdown[source] =
                            (sourceBreakdown[source] || 0) + 1

                        // Add to allVisits with user info
                        allVisits.push({
                            ...visit,
                            visitId,
                            userId,
                            userType: "authenticated",
                            displayTime: formatTimeFull(visit.timestamp),
                        })
                    })
                })
            }

            // Process anonymous visits
            if (anonymousVisitsSnapshot.exists()) {
                const anonymousVisits = anonymousVisitsSnapshot.val()

                // Iterate through anonymous users
                Object.entries(anonymousVisits).forEach(
                    ([anonId, userVisits]) => {
                        // Each anonymous user may have multiple visits
                        Object.entries(userVisits).forEach(
                            ([visitId, visit]) => {
                                totalVisits++

                                // Count by source
                                const source = visit.source || "unknown"
                                sourceBreakdown[source] =
                                    (sourceBreakdown[source] || 0) + 1

                                // Add to allVisits with user info
                                allVisits.push({
                                    ...visit,
                                    visitId,
                                    userId: anonId,
                                    userType: "anonymous",
                                    displayTime: formatTimeFull(
                                        visit.timestamp
                                    ),
                                })
                            }
                        )
                    }
                )
            }

            // Sort all visits by timestamp (newest first)
            allVisits.sort((a, b) => {
                return (
                    normalizeTimestamp(b.timestamp) -
                    normalizeTimestamp(a.timestamp)
                )
            })

            // Get the 50 most recent visits
            const recentVisitsList = allVisits.slice(0, 50)

            // Sort sources by count (descending)
            const sortedSources = Object.entries(sourceBreakdown)
                .sort(([, countA], [, countB]) => countB - countA)
                .reduce((obj, [key, value]) => {
                    obj[key] = value
                    return obj
                }, {})

            setVisitMetrics({
                todayVisits,
                yesterdayVisits,
                totalVisits,
                sourceBreakdown: sortedSources,
            })

            setRecentVisits(recentVisitsList)
        } catch (error) {
            console.error("Error fetching visit metrics:", error)
        }
    }

    // Fetch segmentation data from users
    const fetchSegmentationData = async () => {
        try {
            const usersRef = ref(database, "users")
            const usersSnapshot = await get(usersRef)

            if (usersSnapshot.exists()) {
                const usersData = usersSnapshot.val()
                const stats = calculateUserSegmentation(usersData)
                setSegmentationStats(stats)
            }
        } catch (error) {
            console.error("Error fetching segmentation data:", error)
        }
    }

// Add this function to your MetricsDashboard.js file

// Modified fetchTodaysNewUsers function with enhanced session aggregation
const fetchTodaysNewUsers = async () => {
  try {
      // For testing purposes, we'll extend the timeframe to see more data
      // In production, adjust these to be precise to the current day

      // Calculate time range - using a wider window (past 3 days) for testing
      // You can change this back to 'today' once it's working properly
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 3); // Include 3 days of data for testing
      startDate.setHours(0, 0, 0, 0);
      const startTimestamp = startDate.getTime();

      const endDate = new Date();
      endDate.setHours(23, 59, 59, 999);
      const endTimestamp = endDate.getTime();

      console.log(`Looking for users between: ${new Date(startTimestamp).toLocaleString()} and ${new Date(endTimestamp).toLocaleString()}`);

      // Fetch users data for segmentation
      const usersRef = ref(database, "users");
      const usersSnapshot = await get(usersRef);
      const allUsersData = usersSnapshot.exists() ? usersSnapshot.val() : {};

      // Check both sources of user data - initialClicks and userJourneys
      const userClicksRef = ref(database, "metrics/initialClicks");
      const clicksSnapshot = await get(userClicksRef);

      // Also get user journeys for additional data
      const journeysRef = ref(database, "metrics/userJourneys");
      const journeysSnapshot = await get(journeysRef);

      // Combined user data
      const todaysUserData = [];

      // Track iOS vs Android clicks
      let iosClicks = 0;
      let androidClicks = 0;

      // Debug data
      let totalInitialClicksProcessed = 0;
      let totalJourneysProcessed = 0;
      let totalSessionsProcessed = 0;

      // First process user clicks
      if (clicksSnapshot.exists()) {
          const allClicks = clicksSnapshot.val();
          console.log(`Found ${Object.keys(allClicks).length} users in initialClicks`);

          for (const [userId, userData] of Object.entries(allClicks)) {
              // Check if user was created within our timeframe
              const userCreationTime = normalizeTimestamp(userData.creationTime);
              
              console.log(`User ${userId} creation time: ${new Date(userCreationTime).toLocaleString()}`);
              
              if (
                  userCreationTime &&
                  userCreationTime >= startTimestamp &&
                  userCreationTime <= endTimestamp
              ) {
                  totalInitialClicksProcessed++;
                  console.log(`Processing user ${userId} from initialClicks`);
                  
                  // Process this user's clicks
                  const clicks = userData.clicks
                      ? Object.values(userData.clicks).sort(
                            (a, b) => a.ordinal - b.ordinal
                        )
                      : [];

                  console.log(`User ${userId} has ${clicks.length} initial clicks`);

                  // Count iOS/Android specific actions
                  clicks.forEach((click) => {
                      if (click.action) {
                          if (click.action === "DownloadIOSApp") {
                              iosClicks++;
                          } else if (
                              click.action === "ShowAndroidWaitlist"
                          ) {
                              androidClicks++;
                          }
                      }
                  });

                  // Get segmentation data from users tree
                  const userSegmentationData = allUsersData[userId] || {};

                  todaysUserData.push({
                      userId,
                      email: userData.email || "Unknown",
                      displayName: userData.displayName,
                      creationTime: userCreationTime,
                      clicks: clicks,
                      source: "initialClicks",
                      // Segmentation fields
                      financeAppExperience: userSegmentationData.financeAppExperience,
                      financeAppsUsed: userSegmentationData.financeAppsUsed || [],
                      discoverySource: userSegmentationData.discoverySource,
                      discoverySourceOther: userSegmentationData.discoverySourceOther,
                  });
              }
          }
      }

      // Process journey sessions with improved aggregation
      if (journeysSnapshot.exists()) {
          const allJourneys = journeysSnapshot.val();
          console.log(`Found ${Object.keys(allJourneys).length} users in userJourneys`);

          for (const [userId, journeyData] of Object.entries(allJourneys)) {
              totalJourneysProcessed++;
              
              // Check if this user has any sessions
              if (journeyData.sessions) {
                  const allSessions = Object.entries(journeyData.sessions)
                      .map(([sessionId, session]) => ({
                          sessionId,
                          ...session,
                          // Standardize timestamp format
                          startedAt: normalizeTimestamp(session.startedAt),
                      }))
                      .sort((a, b) => a.startedAt - b.startedAt); // Sort by start time
                  
                  console.log(`User ${userId} has ${allSessions.length} total sessions`);
                  
                  // Find sessions in our time range
                  const filteredSessions = allSessions.filter((session) => {
                      return (
                          session.startedAt >= startTimestamp &&
                          session.startedAt <= endTimestamp
                      );
                  });
                  
                  console.log(`User ${userId} has ${filteredSessions.length} sessions in timeframe`);
                  totalSessionsProcessed += filteredSessions.length;

                  if (filteredSessions.length > 0) {
                      // Determine if this is a new user (any session marked as new OR first session ever)
                      const isNewUser = filteredSessions.some(session => session.isNewUser === true) || 
                                       (filteredSessions[0].startedAt === allSessions[0].startedAt);
                      
                      console.log(`User ${userId} isNewUser: ${isNewUser}`);
                      
                      // ALWAYS process users with sessions in the timeframe
                      let aggregatedClicks = [];
                      
                      // Gather all clicks from all sessions in timeframe
                      for (const session of filteredSessions) {
                          // Add all path steps from this session
                          if (session.path) {
                              const sessionClicks = Array.isArray(session.path) 
                                  ? session.path 
                                  : Object.values(session.path);
                              
                              // Filter out null or undefined clicks
                              const validClicks = sessionClicks.filter(click => click);
                              console.log(`Session ${session.sessionId} has ${validClicks.length} clicks`);
                              
                              aggregatedClicks = [...aggregatedClicks, ...validClicks];
                          }
                      }
                      
                      // Sort aggregated clicks by timestamp
                      aggregatedClicks = aggregatedClicks
                          .filter(click => click && click.timestamp) // Ensure we have valid timestamps
                          .sort((a, b) => {
                              return normalizeTimestamp(a.timestamp) - normalizeTimestamp(b.timestamp);
                          });
                      
                      console.log(`User ${userId} has ${aggregatedClicks.length} total aggregated clicks`);
                      
                      // Re-assign ordinals based on chronological order
                      aggregatedClicks = aggregatedClicks.map((click, index) => ({
                          ...click,
                          ordinal: index + 1,
                          timestamp: normalizeTimestamp(click.timestamp)
                      }));
                      
                      // Count iOS/Android specific actions
                      aggregatedClicks.forEach((click) => {
                          if (click.action) {
                              if (click.action === "DownloadIOSApp") {
                                  iosClicks++;
                              } else if (
                                  click.action === "ShowAndroidWaitlist"
                              ) {
                                  androidClicks++;
                              }
                          }
                      });

                      // Find email from any session if available
                      const email =
                          Object.values(journeyData.sessions).reduce(
                              (found, session) => {
                                  return (
                                      found ||
                                      (session.userEmail
                                          ? session.userEmail
                                          : null)
                                  )
                              },
                              null
                          ) || userId;

                      // Skip users already found in initialClicks (but update their data)
                      const existingUserIndex = todaysUserData.findIndex(
                          (user) => user.userId === userId
                      );

                      if (existingUserIndex >= 0) {
                          // If this user already exists, merge the data
                          const existingClicks = todaysUserData[existingUserIndex].clicks || [];
                          
                          // Combine clicks from both sources, remove duplicates (by screen+action+timestamp)
                          const combinedClicks = [...existingClicks, ...aggregatedClicks];
                          
                          // Get unique clicks based on combination of properties
                          const uniqueClicks = [];
                          const seen = new Set();
                          
                          combinedClicks.forEach(click => {
                              const key = `${click.screen || ''}-${click.action || ''}-${normalizeTimestamp(click.timestamp) || ''}`;
                              if (!seen.has(key)) {
                                  seen.add(key);
                                  uniqueClicks.push(click);
                              }
                          });
                          
                          // Sort and re-assign ordinals
                          const sortedClicks = uniqueClicks
                              .sort((a, b) => normalizeTimestamp(a.timestamp) - normalizeTimestamp(b.timestamp))
                              .map((click, index) => ({
                                  ...click,
                                  ordinal: index + 1
                              }));
                          
                          // Update with the combined data
                          todaysUserData[existingUserIndex].clicks = sortedClicks;
                          todaysUserData[existingUserIndex].source = "combined";
                          todaysUserData[existingUserIndex].isNewUser = isNewUser;
                      } else if (isNewUser) {
                          // Only add new users who aren't in initialClicks
                          todaysUserData.push({
                              userId,
                              email: email,
                              displayName: null,
                              creationTime: filteredSessions[0].startedAt,
                              clicks: aggregatedClicks,
                              source: "journeys",
                              isNewUser: true,
                              sessionCount: filteredSessions.length,
                              sessionIds: filteredSessions.map(session => session.sessionId)
                          });
                      }
                  }
              }
          }
      }

      // Sort by creation time, newest first
      todaysUserData.sort((a, b) => {
          return (
              normalizeTimestamp(b.creationTime) -
              normalizeTimestamp(a.creationTime)
          );
      });

      // Log summary
      console.log(`Processed data:
          - Initial clicks users: ${totalInitialClicksProcessed}
          - Journey users: ${totalJourneysProcessed}
          - Total sessions: ${totalSessionsProcessed}
          - Result user count: ${todaysUserData.length}
          - iOS clicks: ${iosClicks}
          - Android clicks: ${androidClicks}
      `);

      // Update platform stats
      setPlatformStats({
          ios: iosClicks,
          android: androidClicks,
      });

      setTodaysUsers(todaysUserData);
  } catch (error) {
      console.error("Error fetching today's users:", error);
  }
}

    // Helper function to normalize different timestamp formats
    const normalizeTimestamp = (timestamp) => {
        if (!timestamp) return 0

        if (typeof timestamp === "number") {
            return timestamp
        } else if (typeof timestamp === "object") {
            if (timestamp.$date) {
                return new Date(timestamp.$date).getTime()
            } else if (timestamp.toDate) {
                return timestamp.toDate().getTime()
            } else if (timestamp.seconds) {
                // Firestore timestamp
                return timestamp.seconds * 1000
            }
        }

        // Try to parse as date string
        try {
            return new Date(timestamp).getTime()
        } catch (e) {
            console.error("Could not parse timestamp:", timestamp)
            return 0
        }
    }

    const formatTime = (timestamp) => {
        if (!timestamp) return "N/A"
        const date = new Date(normalizeTimestamp(timestamp))
        return date.toLocaleTimeString()
    }

    // Format date and time for visit display
    const formatTimeFull = (timestamp) => {
        if (!timestamp) return "N/A"

        const date = new Date(normalizeTimestamp(timestamp))
        const now = new Date()
        const isToday = date.toDateString() === now.toDateString()

        if (isToday) {
            return `Today at ${date.toLocaleTimeString()}`
        } else {
            return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`
        }
    }

    const toggleUserExpand = (userId) => {
        if (expandedUser === userId) {
            setExpandedUser(null)
        } else {
            setExpandedUser(userId)
        }
    }

    if (loading) {
        return <div>Loading metrics data...</div>
    }

    // Calculate iOS vs Android ratio
    const totalPlatformClicks = platformStats.ios + platformStats.android
    const iosPercentage =
        totalPlatformClicks > 0
            ? Math.round((platformStats.ios / totalPlatformClicks) * 100)
            : 0
    const androidPercentage =
        totalPlatformClicks > 0
            ? Math.round((platformStats.android / totalPlatformClicks) * 100)
            : 0

    // Calculate visits comparison
    const visitsChange =
        visitMetrics.yesterdayVisits > 0
            ? Math.round(
                  ((visitMetrics.todayVisits - visitMetrics.yesterdayVisits) /
                      visitMetrics.yesterdayVisits) *
                      100
              )
            : 0

    return (
        <div className='metrics-dashboard'>
            <h1>User Activity Dashboard</h1>

            <div className='metrics-summary'>
                {/* Visits metrics cards */}
                <div className='metric-card'>
                    <h3>Today's Visits</h3>
                    <div className='metric-value'>
                        {visitMetrics.todayVisits}
                    </div>
                    <div className='metric-detail'>
                        {visitsChange !== 0 && (
                            <span
                                className={
                                    visitsChange >= 0
                                        ? "positive-change"
                                        : "negative-change"
                                }
                            >
                                {visitsChange >= 0 ? "+" : ""}
                                {visitsChange}% from yesterday
                            </span>
                        )}
                    </div>
                    >
                </div>

                <div className='metric-card'>
                    <h3>Total Visits</h3>
                    <div className='metric-value'>
                        {visitMetrics.totalVisits}
                    </div>
                </div>

                <div className='metric-card'>
                    <h3>New Users Today</h3>
                    <div className='metric-value'>{todaysUsers.length}</div>
                </div>

                <div className='metric-card'>
                    <h3>iOS vs Android Clicks</h3>
                    <div className='metric-value'>
                        {iosPercentage}% / {androidPercentage}%
                    </div>
                    <div className='metric-detail'>
                        iOS: {platformStats.ios} | Android:{" "}
                        {platformStats.android}
                    </div>
                </div>

                {/* User Segmentation Cards */}
                <div className='metric-card'>
                    <h3>User Experience</h3>
                    <div className='metric-value'>
                        {segmentationStats ? (
                            <span className='experience-breakdown'>
                                {getExperienceDistribution(segmentationStats).new}% new | {getExperienceDistribution(segmentationStats).occasional}% occasional | {getExperienceDistribution(segmentationStats).powerUser}% power
                            </span>
                        ) : (
                            'Loading...'
                        )}
                    </div>
                    <div className='metric-detail'>
                        {segmentationStats?.total || 0} users with segmentation data
                    </div>
                </div>

                <div className='metric-card'>
                    <h3>Top Finance App</h3>
                    <div className='metric-value'>
                        {segmentationStats && getTopApps(segmentationStats, 1)[0] ? (
                            APP_LABELS[getTopApps(segmentationStats, 1)[0].app] || getTopApps(segmentationStats, 1)[0].app
                        ) : (
                            'N/A'
                        )}
                    </div>
                    <div className='metric-detail'>
                        {segmentationStats && getTopApps(segmentationStats, 1)[0] ? (
                            `${getTopApps(segmentationStats, 1)[0].count} users (${getTopApps(segmentationStats, 1)[0].percentage}%)`
                        ) : (
                            'No data'
                        )}
                    </div>
                </div>
            </div>
{/* User List Section */}
<div className='metrics-section'>
                <h2>Today's New Users</h2>
                {todaysUsers.length > 0 ? (
                    <div className='todays-users-list'>
                        <table>
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>First Click</th>
                                    <th>Total Clicks</th>
                                    <th>Experience</th>
                                    <th>Apps Used</th>
                                    <th>Discovery</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {todaysUsers.map((user) => {
                                    // Check if user has reached DownloadIOSApp or ShowAndroidWaitlist
                                    const reachedPlatformAction =
                                        user.clicks.some(
                                            (click) =>
                                                click.action ===
                                                    "DownloadIOSApp" ||
                                                click.action ===
                                                    "ShowAndroidWaitlist"
                                        )

                                    return (
                                        <React.Fragment key={user.userId}>
                                            <tr>
                                                <td>
                                                    {reachedPlatformAction && (
                                                        <span className='platform-check'>
                                                            <svg
                                                                xmlns='http://www.w3.org/2000/svg'
                                                                viewBox='0 0 24 24'
                                                                width='16'
                                                                height='16'
                                                                fill='none'
                                                                stroke='#2563eb'
                                                                strokeWidth='3'
                                                                strokeLinecap='round'
                                                                strokeLinejoin='round'
                                                            >
                                                                <polyline points='20 6 9 17 4 12' />
                                                            </svg>
                                                        </span>
                                                    )}
                                                    {user.email}
                                                </td>
                                                <td>
                                                    {user.clicks.length > 0
                                                        ? formatTime(
                                                              user.clicks[0]
                                                                  .timestamp
                                                          )
                                                        : "No clicks"}
                                                </td>
                                                <td>{user.clicks.length}</td>
                                                <td>
                                                    {user.financeAppExperience ? (
                                                        <span className={`experience-badge experience-${user.financeAppExperience}`}>
                                                            {user.financeAppExperience === 'power-user' ? 'Power' :
                                                             user.financeAppExperience === 'occasional' ? 'Occasional' : 'New'}
                                                        </span>
                                                    ) : (
                                                        <span className='experience-badge experience-unknown'>—</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {user.financeAppsUsed && user.financeAppsUsed.length > 0 ? (
                                                        <span className='apps-count' title={user.financeAppsUsed.map(app => APP_LABELS[app] || app).join(', ')}>
                                                            {user.financeAppsUsed.length} app{user.financeAppsUsed.length > 1 ? 's' : ''}
                                                        </span>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                                <td>
                                                    {user.discoverySource ? (
                                                        <span className={`source-badge source-${user.discoverySource}`}>
                                                            {user.discoverySource === 'app-store' ? 'Store' :
                                                             user.discoverySource === 'tiktok' ? 'TikTok' : 'Other'}
                                                        </span>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                                <td>
                                                    <button
                                                        onClick={() =>
                                                            toggleUserExpand(
                                                                user.userId
                                                            )
                                                        }
                                                        className='view-details-button'
                                                    >
                                                        {expandedUser ===
                                                        user.userId
                                                            ? "Hide Details"
                                                            : "View Details"}
                                                    </button>
                                                </td>
                                            </tr>
                                            {expandedUser === user.userId && (
                                                <tr className='user-details-row'>
                                                    <td colSpan='7'>
                                                        <div className='user-click-details'>
                                                            <h4>
                                                                Click Journey{" "}
                                                                {user.source ===
                                                                    "journeys" &&
                                                                    `(Session ID: ${user.sessionId})`}
                                                            </h4>
                                                            {user.clicks
                                                                .length ===
                                                            0 ? (
                                                                <p>
                                                                    No clicks
                                                                    recorded yet
                                                                </p>
                                                            ) : (
                                                                <table className='clicks-table'>
                                                                    <thead>
                                                                        <tr>
                                                                            <th>
                                                                                #
                                                                            </th>
                                                                            <th>
                                                                                Screen
                                                                            </th>
                                                                            <th>
                                                                                Action
                                                                            </th>
                                                                            <th>
                                                                                Time
                                                                            </th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {user.clicks.map(
                                                                            (
                                                                                click,
                                                                                index
                                                                            ) => (
                                                                                <tr
                                                                                    key={
                                                                                        index
                                                                                    }
                                                                                >
                                                                                    <td>
                                                                                        {click.ordinal ||
                                                                                            index +
                                                                                                1}
                                                                                    </td>
                                                                                    <td>
                                                                                        {
                                                                                            click.screen
                                                                                        }
                                                                                    </td>
                                                                                    <td>
                                                                                        {
                                                                                            click.action
                                                                                        }
                                                                                    </td>
                                                                                    <td>
                                                                                        {formatTime(
                                                                                            click.timestamp
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                            )
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className='no-users-message'>No new users today</div>
                )}
            </div>

            {/* User Segmentation Section */}
            {segmentationStats && (
                <div className='metrics-section'>
                    <h2>User Segmentation</h2>

                    {/* Experience Distribution */}
                    <div className='segmentation-container'>
                        <h3>By Experience Level</h3>
                        <div className='segmentation-bars'>
                            {Object.entries(segmentationStats.byExperience).map(([level, count]) => {
                                const percentage = segmentationStats.total > 0
                                    ? Math.round((count / segmentationStats.total) * 100)
                                    : 0;
                                return (
                                    <div key={level} className='segmentation-bar-container'>
                                        <div className='segmentation-info'>
                                            <span className='segmentation-name'>
                                                {EXPERIENCE_LABELS[level] || level}
                                            </span>
                                            <span className='segmentation-count'>
                                                {count} users ({percentage}%)
                                            </span>
                                        </div>
                                        <div className='segmentation-bar-bg'>
                                            <div
                                                className={`segmentation-bar experience-${level}`}
                                                style={{ width: `${percentage}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Top Apps */}
                    <div className='segmentation-container' style={{ marginTop: '24px' }}>
                        <h3>Most Used Finance Apps</h3>
                        <div className='apps-grid'>
                            {getTopApps(segmentationStats, 6).map(({ app, count, percentage }) => (
                                <div key={app} className='app-card'>
                                    <div className='app-name'>{APP_LABELS[app] || app}</div>
                                    <div className='app-count'>{count}</div>
                                    <div className='app-percentage'>{percentage}%</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Traffic Sources Section */}
            <div className='metrics-section'>
                <h2>Traffic Sources</h2>
                <div className='traffic-sources'>
                    {Object.keys(visitMetrics.sourceBreakdown).length > 0 ? (
                        <div className='source-bars'>
                            {Object.entries(visitMetrics.sourceBreakdown).map(
                                ([source, count]) => {
                                    const percentage = Math.round(
                                        (count / visitMetrics.totalVisits) * 100
                                    )
                                    return (
                                        <div
                                            key={source}
                                            className='source-bar-container'
                                        >
                                            <div className='source-info'>
                                                <span className='source-name'>
                                                    {source}
                                                </span>
                                                <span className='source-count'>
                                                    {count} visits ({percentage}
                                                    %)
                                                </span>
                                            </div>
                                            <div className='source-bar-bg'>
                                                <div
                                                    className='source-bar'
                                                    style={{
                                                        width: `${percentage}%`,
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                    )
                                }
                            )}
                        </div>
                    ) : (
                        <p>No traffic source data available</p>
                    )}
                </div>
            </div>
            <UserPathsVisualization />

            {/* NEW SECTION: Recent Visits with Timestamps */}
            <div className='metrics-section'>
                <h2>Recent Visits</h2>
                <div className='recent-visits-container'>
                    {recentVisits.length > 0 ? (
                        <table className='recent-visits-table'>
                            <thead>
                                <tr>
                                    <th>Timestamp</th>
                                    <th>User ID</th>
                                    <th>Source</th>
                                    <th>Device</th>
                                    <th>Type</th>
                                    <th>Timezone</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentVisits.map((visit, index) => (
                                    <tr
                                        key={`${visit.userId}-${
                                            visit.visitId || index
                                        }`}
                                    >
                                        <td className='timestamp-cell'>
                                            {visit.displayTime}
                                        </td>
                                        <td className='user-id-cell'>
                                            {visit.userId.substring(0, 12)}...
                                        </td>
                                        <td className='source-cell'>
                                            <span
                                                className={`source-badge source-${visit.source}`}
                                            >
                                                {visit.source || "Unknown"}
                                            </span>
                                        </td>
                                        <td className='device-cell'>
                                            {visit.deviceType || "Unknown"}
                                        </td>
                                        <td className='type-cell'>
                                            <span
                                                className={`type-badge ${visit.userType}`}
                                            >
                                                {visit.userType}
                                            </span>
                                        </td>
                                        <td className='timezone-cell'>
                                            {visit.timezone ? (
                                                <span
                                                    title={`${
                                                        visit.timezone.name
                                                    } (UTC${
                                                        visit.timezone
                                                            .offsetHours >= 0
                                                            ? "+"
                                                            : ""
                                                    }${
                                                        visit.timezone
                                                            .offsetHours
                                                    })`}
                                                >
                                                    {visit.timezone
                                                        .friendlyName ||
                                                        visit.timezone.name
                                                            .split("/")
                                                            .pop()
                                                            .replace(/_/g, " ")}
                                                </span>
                                            ) : (
                                                "Unknown"
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p>No recent visits available</p>
                    )}
                </div>
            </div>

            

            <style jsx>{`
                .metrics-dashboard {
                    padding: 20px;
                    max-width: 1200px;
                    margin: 0 auto;
                }

                h2 {
                    font-size: 1.5rem;
                    margin-top: 30px;
                    margin-bottom: 15px;
                    color: #111827;
                    border-bottom: 1px solid #e5e7eb;
                    padding-bottom: 8px;
                }

                .metrics-summary {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 20px;
                    margin-bottom: 30px;
                }

                .metric-card {
                    background: #f8f9fa;
                    border-radius: 8px;
                    padding: 16px;
                    flex: 1;
                    min-width: 200px;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                }

                .metric-value {
                    font-size: 28px;
                    font-weight: bold;
                    color: #2563eb;
                }

                .metric-detail {
                    font-size: 14px;
                    color: #666;
                    margin-top: 8px;
                }

                .positive-change {
                    color: #10b981;
                }

                .negative-change {
                    color: #ef4444;
                }

                .metrics-section {
                    margin-bottom: 30px;
                }

                .traffic-sources {
                    background-color: white;
                    border-radius: 8px;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                    padding: 20px;
                }

                .source-bars {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .source-bar-container {
                    width: 100%;
                }

                .source-info {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 4px;
                }

                .source-name {
                    font-weight: 500;
                    text-transform: capitalize;
                }

                .source-count {
                    font-size: 14px;
                    color: #4b5563;
                }

                .source-bar-bg {
                    width: 100%;
                    height: 8px;
                    background-color: #e5e7eb;
                    border-radius: 4px;
                    overflow: hidden;
                }

                .source-bar {
                    height: 100%;
                    background-color: #2563eb;
                    border-radius: 4px;
                    transition: width 0.5s ease;
                }

                /* Recent Visits Styles */
                .recent-visits-container {
                    background-color: white;
                    border-radius: 8px;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                    overflow-x: auto;
                }

                .recent-visits-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 14px;
                }

                .recent-visits-table th,
                .recent-visits-table td {
                    padding: 10px 12px;
                    text-align: left;
                    border-bottom: 1px solid #e5e7eb;
                }

                .recent-visits-table th {
                    background-color: #f9fafb;
                    font-weight: 600;
                    position: sticky;
                    top: 0;
                }

                .timestamp-cell {
                    width: 200px;
                    white-space: nowrap;
                }

                .user-id-cell {
                    width: 180px;
                    font-family: monospace;
                    font-size: 12px;
                }

                .source-cell {
                    width: 120px;
                }

                .device-cell {
                    width: 100px;
                }

                .type-cell {
                    width: 120px;
                }

                .source-badge {
                    display: inline-block;
                    padding: 2px 8px;
                    border-radius: 12px;
                    font-size: 12px;
                    text-transform: capitalize;
                    background-color: #e5e7eb;
                }

                .source-direct {
                    background-color: #e5e7eb;
                }

                .source-google {
                    background-color: #e8f0fe;
                    color: #1a73e8;
                }

                .source-facebook {
                    background-color: #e7f3ff;
                    color: #1877f2;
                }

                .source-instagram {
                    background-color: #fbe9f3;
                    color: #c13584;
                }

                .source-tiktok {
                    background-color: #f0f0f0;
                    color: #000000;
                }

                .type-badge {
                    display: inline-block;
                    padding: 2px 8px;
                    border-radius: 12px;
                    font-size: 12px;
                }

                .authenticated {
                    background-color: #dcfce7;
                    color: #15803d;
                }

                .anonymous {
                    background-color: #fff1f2;
                    color: #be123c;
                }

                .todays-users-list {
                    background-color: white;
                    border-radius: 8px;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                    overflow: hidden;
                }

                .todays-users-list table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .todays-users-list th,
                .todays-users-list td {
                    padding: 12px 16px;
                    text-align: left;
                    border-bottom: 1px solid #e5e7eb;
                }

                .todays-users-list th {
                    background-color: #f9fafb;
                    font-weight: 600;
                }

                .view-details-button {
                    background-color: #e5e7eb;
                    border: none;
                    border-radius: 4px;
                    padding: 4px 8px;
                    cursor: pointer;
                    font-size: 12px;
                }

                .view-details-button:hover {
                    background-color: #d1d5db;
                }

                .user-details-row {
                    background-color: #f9fafb;
                }

                .user-click-details {
                    padding: 16px;
                }

                .user-click-details h4 {
                    margin-top: 0;
                    margin-bottom: 12px;
                    font-size: 14px;
                    color: #4b5563;
                }

                .clicks-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 13px;
                }

                .clicks-table th,
                .clicks-table td {
                    padding: 8px;
                    border: 1px solid #e5e7eb;
                }

                .clicks-table th {
                    background-color: #f3f4f6;
                }

                .no-users-message {
                    padding: 20px;
                    text-align: center;
                    color: #6b7280;
                    background-color: white;
                    border-radius: 8px;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                }

                .platform-check {
                    display: inline-flex;
                    margin-right: 8px;
                    vertical-align: middle;
                }

                /* User Segmentation Styles */
                .experience-breakdown {
                    font-size: 14px;
                    line-height: 1.4;
                }

                .segmentation-container {
                    background-color: white;
                    border-radius: 8px;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                    padding: 20px;
                }

                .segmentation-container h3 {
                    font-size: 16px;
                    margin-bottom: 16px;
                    color: #111827;
                    border-bottom: none;
                    padding-bottom: 0;
                }

                .segmentation-bars {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .segmentation-bar-container {
                    width: 100%;
                }

                .segmentation-info {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 4px;
                }

                .segmentation-name {
                    font-weight: 500;
                }

                .segmentation-count {
                    font-size: 14px;
                    color: #4b5563;
                }

                .segmentation-bar-bg {
                    width: 100%;
                    height: 8px;
                    background-color: #e5e7eb;
                    border-radius: 4px;
                    overflow: hidden;
                }

                .segmentation-bar {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 0.5s ease;
                }

                .experience-new {
                    background-color: #10b981;
                }

                .experience-occasional {
                    background-color: #f59e0b;
                }

                .experience-power-user {
                    background-color: #8b5cf6;
                }

                .experience-unknown {
                    background-color: #6b7280;
                }

                .apps-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
                    gap: 12px;
                }

                .app-card {
                    background-color: #f9fafb;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 12px;
                    text-align: center;
                }

                .app-name {
                    font-weight: 600;
                    font-size: 14px;
                    margin-bottom: 4px;
                }

                .app-count {
                    font-size: 20px;
                    font-weight: 700;
                    color: #2563eb;
                    margin-bottom: 2px;
                }

                .app-percentage {
                    font-size: 12px;
                    color: #6b7280;
                }

                /* Table badges */
                .experience-badge {
                    display: inline-block;
                    padding: 2px 8px;
                    border-radius: 12px;
                    font-size: 11px;
                    font-weight: 600;
                    text-transform: uppercase;
                }

                .experience-badge.experience-new {
                    background-color: #d1fae5;
                    color: #065f46;
                }

                .experience-badge.experience-occasional {
                    background-color: #fef3c7;
                    color: #92400e;
                }

                .experience-badge.experience-power-user {
                    background-color: #ede9fe;
                    color: #5b21b6;
                }

                .experience-badge.experience-unknown {
                    background-color: #f3f4f6;
                    color: #6b7280;
                }

                .apps-count {
                    font-size: 12px;
                    color: #4b5563;
                    cursor: help;
                }

                .source-badge {
                    display: inline-block;
                    padding: 2px 8px;
                    border-radius: 12px;
                    font-size: 11px;
                    font-weight: 600;
                }

                .source-tiktok {
                    background-color: #f0f0f0;
                    color: #000000;
                }

                .source-app-store {
                    background-color: #e8f0fe;
                    color: #1a73e8;
                }

                .source-other {
                    background-color: #f3f4f6;
                    color: #6b7280;
                }
            `}</style>
        </div>
    )
}

export default MetricsDashboard
